import { useEffect, useMemo } from "react";
import { Button } from "components/ui/button";
import { DialogClose } from "components/ui/dialog";
import { LinkIcon } from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import AppDialog from "components/AppDialog";
import {
  usePerfectFitJobs,
  PerfectFitJobsTable,
  PerfectFitJobHeader,
} from "components/PerfectFitJobs/shared";
import { Table, TableBody, TableCell, TableRow } from "components/ui/table";
import UserIdentity from "./UserIdentity";
import UserContextColumns from "./UserContextColumns";

export default function PerfectFitJobModal({
  isDialogOpen,
  setDialogVisibility,
  user,
}) {
  const { id, isUnclaimed } = user;
  const userName = useMemo(
    () =>
      user.fullName ||
      `${user.given_name || ""} ${user.family_name || ""}`.trim(),
    [user.fullName, user.family_name, user.given_name]
  );

  const {
    selectedJobs,
    creatingMatches,
    expandedJobId,
    setExpandedJobId,
    perfectFitJobs,
    jobsLoading,
    loadPerfectFitJobs,
    getJobLocation,
    getJobRateSalary,
    getJobTalentRateSalary,
    getPerfectJobFitReason,
    handleSelectAll,
    handleSelectRow,
    createMatchForJobs,
  } = usePerfectFitJobs(id);

  useEffect(() => {
    if (isDialogOpen) {
      loadPerfectFitJobs();
    }
  }, [isDialogOpen, loadPerfectFitJobs]);

  useEffect(() => {
    if (!isDialogOpen) {
      setExpandedJobId(null);
    }
  }, [isDialogOpen, setExpandedJobId]);

  const handleClose = () => {
    setDialogVisibility(false);
  };

  const handleCreateMatches = async () => {
    await createMatchForJobs(id, isUnclaimed, handleClose);
  };

  return (
    <AppDialog
      open={isDialogOpen}
      onOpenChange={setDialogVisibility}
      onClose={handleClose}
      header={`Find Perfect Fit Jobs for ${userName}`}
      className="flex max-h-[90vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-lg lg:min-w-[55vw]"
      contentClassName="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 pt-4"
    >
      <UserIdentity user={user} />

      <UserContextColumns user={user} shouldFetch={isDialogOpen} />

      <div className="-mx-2 no-scrollbar max-h-[30vh] overflow-y-auto px-2">
        {jobsLoading ? (
          <Table>
            <PerfectFitJobHeader disabled={true} />
            <TableBody>
              <TableRow>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-6 bg-foreground/5" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <PerfectFitJobsTable
            perfectFitJobs={perfectFitJobs}
            jobsLoading={jobsLoading}
            selectedJobs={selectedJobs}
            expandedJobId={expandedJobId}
            setExpandedJobId={setExpandedJobId}
            handleSelectAll={handleSelectAll}
            handleSelectRow={handleSelectRow}
            getJobLocation={getJobLocation}
            getJobRateSalary={getJobRateSalary}
            getJobTalentRateSalary={getJobTalentRateSalary}
            getPerfectJobFitReason={getPerfectJobFitReason}
          />
        )}
      </div>

      <div className="flex shrink-0 justify-end gap-2">
        <DialogClose asChild>
          <Button variant="outline" disabled={creatingMatches}>
            Close
          </Button>
        </DialogClose>
        <Button
          disabled={jobsLoading || selectedJobs.size === 0 || creatingMatches}
          onClick={handleCreateMatches}
        >
          <LinkIcon className="mr-2 h-4 w-4" />
          {jobsLoading && <>Loading...</>}
          {!jobsLoading && !creatingMatches && (
            <>Match to {selectedJobs.size} Job(s)</>
          )}
          {!jobsLoading && creatingMatches && <>Creating Matches...</>}
        </Button>
      </div>
    </AppDialog>
  );
}
